import { useCallback, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { RgbaStringColorPicker } from 'react-colorful';
import get from 'lodash/get';

import { FormError, HelpText } from '@Forms/fields.jsx';
import { useClickOutside } from '@hooks';

import './ColorField.scss';

const validateColorValue = (color) => {
  if (!color || typeof color !== 'string') {
    return { isValid: false, message: 'Color value is required' };
  }

  const trimmedColor = color.trim();

  // Validate hex format (#RRGGBB or #RGB)
  if (trimmedColor.startsWith('#')) {
    const hexPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
    if (!hexPattern.test(trimmedColor)) {
      return {
        isValid: false,
        message: 'Invalid hex format. Use #RGB or #RRGGBB',
      };
    }
    return { isValid: true };
  }

  // Validate rgb() format
  if (trimmedColor.startsWith('rgb(')) {
    const rgbPattern = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/;
    const match = trimmedColor.match(rgbPattern);
    if (!match) {
      return {
        isValid: false,
        message: 'Invalid rgb format. Use rgb(r, g, b)',
      };
    }

    const [, r, g, b] = match;
    if (r > 255 || g > 255 || b > 255) {
      return {
        isValid: false,
        message: 'RGB values must be between 0 and 255',
      };
    }
    return { isValid: true };
  }

  // Validate rgba() format
  if (trimmedColor.startsWith('rgba(')) {
    const rgbaPattern =
      /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([01]?(?:\.\d+)?)\s*\)$/;
    const match = trimmedColor.match(rgbaPattern);
    if (!match) {
      return {
        isValid: false,
        message: 'Invalid rgba format. Use rgba(r, g, b, a)',
      };
    }

    const [, r, g, b, a] = match;
    if (r > 255 || g > 255 || b > 255) {
      return {
        isValid: false,
        message: 'RGB values must be between 0 and 255',
      };
    }
    if (parseFloat(a) > 1) {
      return { isValid: false, message: 'Alpha value must be between 0 and 1' };
    }
    return { isValid: true };
  }

  return {
    isValid: false,
    message: 'Invalid color format. Use hex (#RRGGBB), rgb(), or rgba()',
  };
};

const normalizeToRgba = (color) => {
  if (!color) return color;

  if (color.startsWith('rgba')) {
    return color;
  }

  if (color.startsWith('rgb(')) {
    // Convert rgb to rgba by adding alpha 1
    return color.replace('rgb(', 'rgba(').replace(')', ', 1)');
  }

  if (color.startsWith('#')) {
    // Convert hex to rgba
    const hex = color.replace('#', '').trim();
    let r, g, b;

    if (hex.length === 3) {
      // Short hex format (#RGB)
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      // Full hex format (#RRGGBB)
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    } else {
      return color; // Invalid hex format, return as is
    }

    return `rgba(${r}, ${g}, ${b}, 1)`;
  }

  return color;
};

const ColorField = ({
  className,
  helpText,
  label,
  name,
  placeholder = 'rgba(0, 0, 0, 1)',
  useFormReturn,
  options = {},
  ...props
}) => {
  const popover = useRef();
  const [isOpen, toggle] = useState(false);

  // unpacked form vars from useForm
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormReturn;

  const error = get(errors, name);

  const close = useCallback(() => {
    toggle(false);
    // Trigger validation for this field when popover closes
    trigger(name);
  }, []);
  useClickOutside(popover, close);

  return (
    <div
      className={
        `sb-form-item-formgroup sb-form-item-input ` +
        (error ? 'sb-form-item-error' : '') +
        ' ' +
        (className ? className : '')
      }
    >
      {label && <label htmlFor={name}>{label}</label>}
      <div className="sb-form-item-content">
        <Controller
          control={control}
          name={name}
          rules={{
            ...options,
            validate: (value) => {
              if (!value) return true; // Allow empty values if not required
              const validation = validateColorValue(value);
              return validation.isValid || validation.message;
            },
          }}
          render={({ field: controllerField }) => (
            <div className="color-picker">
              <div className="color-swatch" onClick={() => toggle(true)}>
                <div
                  className="color-swatch-inner"
                  style={{
                    backgroundColor: controllerField.value || placeholder,
                  }}
                />
              </div>
              {isOpen && (
                <div className="color-popover" ref={popover}>
                  <RgbaStringColorPicker
                    color={
                      normalizeToRgba(controllerField.value) || placeholder
                    }
                    onChange={controllerField.onChange}
                  />
                  <input
                    type="text"
                    className="color-input"
                    spellCheck="false"
                    value={
                      normalizeToRgba(controllerField.value) || placeholder
                    }
                    onChange={(e) => {
                      const value = normalizeToRgba(e.target.value);
                      controllerField.onChange(value);
                    }}
                  />
                </div>
              )}
            </div>
          )}
        />
        {helpText && <HelpText message={helpText} />}
        {error && <FormError message={error.message} />}
      </div>
    </div>
  );
};

export default ColorField;
